import { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { Fuel, Camera, X, Loader2, CheckCircle2, ImagePlus } from 'lucide-react';
import { api } from '../../lib/auth';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from '../ui/dialog';

const MAX = 10;

function Thumbs({ files, onRemove }) {
  const [urls, setUrls] = useState([]);
  useEffect(() => { const u = files.map((f) => URL.createObjectURL(f)); setUrls(u); return () => u.forEach(URL.revokeObjectURL); }, [files]);
  if (!files.length) return null;
  return (
    <div className="grid grid-cols-3 gap-2" data-testid="fuel-thumbs">
      {urls.map((u, i) => (
        <div key={u} className="relative aspect-[3/4] rounded-xl overflow-hidden border border-slate-200 bg-slate-100">
          <img src={u} alt="" className="h-full w-full object-cover" />
          <button type="button" onClick={() => onRemove(i)} className="absolute top-1 right-1 h-7 w-7 rounded-full bg-black/60 text-white flex items-center justify-center" data-testid={`fuel-thumb-remove-${i}`}><X className="h-4 w-4" /></button>
        </div>
      ))}
    </div>
  );
}

function FuelForm({ plate, onDone }) {
  const [km, setKm] = useState('');
  const [note, setNote] = useState('');
  const [plateIn, setPlateIn] = useState('');
  const [files, setFiles] = useState([]);
  const [drag, setDrag] = useState(false);
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const input = useRef(null);
  const cam = useRef(null);
  const add = (list) => {
    const imgs = [...list].filter((f) => f.type.startsWith('image/'));
    if (imgs.length < list.length) toast.error('Sadece resim yüklenebilir');
    setFiles((p) => { const n = [...p, ...imgs].slice(0, MAX); if (p.length + imgs.length > MAX) toast.error(`En fazla ${MAX} fiş`); return n; });
  };
  useEffect(() => {
    const onPaste = (e) => { const fs = [...(e.clipboardData?.files || [])]; if (fs.length) { e.preventDefault(); add(fs); } };
    window.addEventListener('paste', onPaste);
    return () => window.removeEventListener('paste', onPaste);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const submit = async () => {
    if (!files.length) return toast.error('En az bir fiş görüntüsü ekleyin');
    setBusy(true);
    try {
      const fd = new FormData();
      if (km) fd.append('km', km);
      if (note.trim()) fd.append('note', note.trim());
      if (!plate && plateIn.trim()) fd.append('plate', plateIn.trim());
      files.forEach((f) => fd.append('files', f));
      await api.post('/fuel-submissions', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setSent(true);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Gönderilemedi'); }
    finally { setBusy(false); }
  };
  if (sent) {
    return (
      <div className="py-10 text-center space-y-4" data-testid="fuel-sent">
        <CheckCircle2 className="h-20 w-20 mx-auto text-emerald-500" />
        <div><h3 className="text-2xl font-black">Gönderildi</h3><p className="text-sm text-slate-500 mt-1">{files.length} fiş yöneticinize iletildi.</p></div>
        <button type="button" onClick={onDone} className="w-full h-14 rounded-2xl bg-slate-900 text-white text-lg font-bold" data-testid="fuel-sent-ok">Tamam</button>
      </div>
    );
  }
  return (
    <div className="space-y-4">
      {plate ? <div className="rounded-xl bg-slate-100 dark:bg-slate-800 px-3 py-2 text-sm">Araç: <b className="tabular-nums">{plate}</b></div> : (
        <label className="block">
          <span className="text-sm font-semibold">Plaka <span className="text-slate-400 font-normal">(araç atanmamış)</span></span>
          <input value={plateIn} onChange={(e) => setPlateIn(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, ''))} maxLength={15} placeholder="örn. 1ABC123"
            className="mt-1 w-full h-12 rounded-xl border-2 border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 px-4 text-lg font-bold tabular-nums focus:border-orange-500 outline-none" data-testid="fuel-plate-input" />
        </label>
      )}
      <label className="block">
        <span className="text-sm font-semibold">Aracın KM'si <span className="text-slate-400 font-normal">(isteğe bağlı)</span></span>
        <input value={km} onChange={(e) => setKm(e.target.value.replace(/[^\d]/g, ''))} inputMode="numeric" placeholder="örn. 135650"
          className="mt-1 w-full h-14 rounded-xl border-2 border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 px-4 text-2xl font-bold tabular-nums focus:border-orange-500 outline-none" data-testid="fuel-km-input" />
      </label>
      <div
        onDragOver={(e) => { e.preventDefault(); setDrag(true); }} onDragLeave={() => setDrag(false)}
        onDrop={(e) => { e.preventDefault(); setDrag(false); add(e.dataTransfer.files); }}
        className={`rounded-2xl border-2 border-dashed p-5 text-center transition-colors ${drag ? 'border-orange-500 bg-orange-50' : 'border-slate-300 dark:border-slate-700'}`} data-testid="fuel-dropzone">
        <ImagePlus className="h-10 w-10 mx-auto text-slate-400" />
        <p className="text-sm font-semibold mt-1">Mazot fişi görüntüleri</p>
        <p className="text-[11px] text-slate-500">Sürükle-bırak, yapıştır (Ctrl+V) veya seç · en fazla {MAX}</p>
        <div className="mt-3 grid grid-cols-2 gap-2">
          <button type="button" onClick={() => cam.current?.click()} className="h-12 rounded-xl bg-orange-500 text-white font-bold inline-flex items-center justify-center gap-2" data-testid="fuel-camera-btn"><Camera className="h-5 w-5" />Fotoğraf çek</button>
          <button type="button" onClick={() => input.current?.click()} className="h-12 rounded-xl border-2 border-slate-200 dark:border-slate-700 font-bold" data-testid="fuel-pick-btn">Galeriden seç</button>
        </div>
        <input ref={cam} type="file" accept="image/*" capture="environment" className="hidden" onChange={(e) => { add(e.target.files); e.target.value = ''; }} />
        <input ref={input} type="file" accept="image/*" multiple className="hidden" onChange={(e) => { add(e.target.files); e.target.value = ''; }} data-testid="fuel-file-input" />
      </div>
      <Thumbs files={files} onRemove={(i) => setFiles((p) => p.filter((_, j) => j !== i))} />
      <input value={note} onChange={(e) => setNote(e.target.value)} maxLength={500} placeholder="Not (isteğe bağlı)"
        className="w-full h-9 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 text-sm focus:border-orange-500 outline-none" data-testid="fuel-note-input" />
      <button type="button" onClick={submit} disabled={busy || !files.length}
        className="w-full h-16 rounded-2xl bg-emerald-600 text-white text-xl font-black disabled:opacity-40 inline-flex items-center justify-center gap-2 active:scale-[0.98] transition-transform" data-testid="fuel-submit">
        {busy && <Loader2 className="h-6 w-6 animate-spin" />}GÖNDER
      </button>
    </div>
  );
}

export function FuelFormCard({ compact = false }) {
  const [info, setInfo] = useState(null);
  const [open, setOpen] = useState(false);
  const [key, setKey] = useState(0);
  useEffect(() => { api.get('/fuel-submissions/my/eligible').then((r) => setInfo(r.data.data)).catch(() => {}); }, []);
  if (!info?.eligible || compact !== !!info.is_admin) return null;
  const openForm = () => { setKey((k) => k + 1); setOpen(true); };
  return (
    <div className={compact ? '' : 'mb-4'}>
      {compact ? (
        <button type="button" onClick={openForm} className="h-8 px-3 rounded-lg bg-orange-500 hover:bg-orange-600 text-white text-xs font-semibold inline-flex items-center gap-1.5 transition-colors" data-testid="fuel-form-btn-compact">
          <Fuel className="h-3.5 w-3.5" />Mazot Formu
        </button>
      ) : (
      <button type="button" onClick={openForm}
        className="w-full rounded-2xl bg-gradient-to-r from-orange-500 to-amber-500 text-white p-4 flex items-center gap-3 shadow-md active:scale-[0.99] transition-transform" data-testid="fuel-form-btn">
        <span className="h-12 w-12 rounded-xl bg-white/20 flex items-center justify-center"><Fuel className="h-7 w-7" /></span>
        <span className="text-left"><span className="block text-lg font-black">Mazot Formu</span><span className="block text-xs opacity-90">Fiş fotoğrafını gönder</span></span>
      </button>
      )}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md max-h-[92vh] overflow-y-auto" data-testid="fuel-form-dialog">
          <DialogTitle className="flex items-center gap-2"><Fuel className="h-5 w-5 text-orange-500" />Mazot Formu</DialogTitle>
          <DialogDescription>Fişin fotoğrafını çekin, KM'yi yazın ve gönderin.</DialogDescription>
          <FuelForm key={key} plate={info.plate} onDone={() => setOpen(false)} />
        </DialogContent>
      </Dialog>
    </div>
  );
}
